import React, { useState, useEffect, useRef, lazy, Suspense } from 'react';
const MeshPreviewViewer = lazy(() => import('./MeshPreviewViewer'));
import {
  Settings,
  Plus,
  X,
  Folder,
  Link2,
  Grid3x3,
  Server,
  Zap,
  RefreshCw,
  ChevronLeft,
  ChevronRight,
  Loader2,
  Box,
  Image as ImageIcon,
  Video,
  Music,
  AlertCircle,
  CheckCircle2,
  Edit2,
  Save,
  Info,
  FileText,
  Upload,
  Eye,
  Play,
  Headphones,
  Maximize2,
} from 'lucide-react';
import { ASSET_FILE_TYPES } from '../constants/trainingConstants';

// Mounts viewer only while the card is visible; unmounts when scrolled away.
// This keeps active WebGL context count naturally bounded to what's on screen.
function VisibleMeshPreview({ url, height = '144px', retryKey = 0, onBlobReady }) {
  const ref = useRef(null);
  const [inView, setInView] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    // Reset only when retryKey changes (explicit retry), not on initial mount.
    // This avoids blanking cards that are already visible.
    if (retryKey > 0) setInView(false);
    const obs = new IntersectionObserver(
      ([entry]) => setInView(entry.isIntersecting),
      { threshold: 0.01 }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, [retryKey]);

  return (
    <div ref={ref} style={{ height, width: '100%' }}>
      {inView ? (
        <Suspense fallback={<div className="flex items-center justify-center h-full"><Loader2 className="w-5 h-5 animate-spin text-gray-400" /></div>}>
          <MeshPreviewViewer key={retryKey} url={url} style={{ height, width: '100%' }} onBlobReady={onBlobReady} />
        </Suspense>
      ) : (
        <div className="flex items-center justify-center h-full">
          <Loader2 className="w-5 h-5 animate-spin text-gray-400" />
        </div>
      )}
    </div>
  );
}

export default function AssetSelectionStep({
  loadedAssets,
  requiredAssets,
  analyzedAssets = [],
  assetViewMode,
  manualEntryText = '',
  dragActiveAssets,
  analyzingAssets,
  gridPageAssets = [],
  loadingGridAssets = false,
  expandedAssetId,
  combinedContent,
  autoFetchingAssets = false,
  fetchProgress = null,
  fetchingAssetId = null,
  onManualEntryChange,
  onManualEntryAdd,
  onAssetRemove,
  onRequiredAssetAdd,
  onRequiredAssetRemove,
  onViewModeChange,
  onDragAssets,
  onDropAssets,
  onReanalyze,
  onAutoFetchAssets,
  onAddGridPageAsset,
  onAssetInfoUpdate,
  onAddAnalyzedAsset,
  onUploadAssetForAnalyzed,
  onExpandedAssetChange,
  assetPicker = { open: false, asset: null, candidates: [], searching: false, searchStatus: '', previews: [], selectedPreviewIdx: null },
  onAiAutoFetch,
  onSelectPreview,
  onRetryPreview,
  onConfirmSelectedPreview,
  onCloseAssetPicker,
  onBack,
  onNext,
}) {
  const [editingAssetId, setEditingAssetId] = useState(null);
  const [editDescription, setEditDescription] = useState('');
  const [editUsage, setEditUsage] = useState('');
  const [previewAsset, setPreviewAsset] = useState(null);
  const [expandedPreviewIdx, setExpandedPreviewIdx] = useState(null);
  const [previewRetryKeys, setPreviewRetryKeys] = useState({});
  const [failedThumbnails, setFailedThumbnails] = useState(new Set());
  const [activeEmbedKey, setActiveEmbedKey] = useState(null); // only 1 WebGL context at a time
  // uid → blob URL; avoids re-downloading when expanding a card that already previewed
  const blobUrlCacheRef = useRef({});

  // Reset embed + thumbnails when picker opens/closes
  useEffect(() => {
    if (assetPicker.open) {
      setFailedThumbnails(new Set());
    } else {
      setActiveEmbedKey(null);
      // Revoke all cached blob URLs to free memory
      Object.values(blobUrlCacheRef.current).forEach(u => {
        try { URL.revokeObjectURL(u); } catch (_) {}
      });
      blobUrlCacheRef.current = {};
    }
  }, [assetPicker.open]);

  const getAssetIcon = (assetType) => {
    switch (assetType) {
      case 'mesh':
        return <Box className="w-4 h-4 text-blue-400" />;
      case 'image':
        return <ImageIcon className="w-4 h-4 text-green-400" />;
      case 'video':
        return <Video className="w-4 h-4 text-purple-400" />;
      case 'audio':
        return <Music className="w-4 h-4 text-yellow-400" />;
      default:
        return <Folder className="w-4 h-4 text-gray-400" />;
    }
  };

  const getAssetThumbnail = (asset) => {
    const type = asset.assetType || asset.type;
    const url = asset.url;

    if (type === 'image' && url) {
      return (
        <img
          src={url}
          alt={asset.name}
          className="w-full h-full object-cover"
          onError={(e) => { e.target.style.display = 'none'; }}
        />
      );
    }
    if (type === 'video' && url) {
      return (
        <>
          <video
            src={url}
            className="w-full h-full object-cover"
            muted
            preload="metadata"
          />
          <div className="absolute inset-0 flex items-center justify-center bg-black/30">
            <Play className="w-4 h-4 text-white" />
          </div>
        </>
      );
    }
    if (type === 'audio') {
      return <Headphones className="w-6 h-6 text-yellow-400" />;
    }
    return getAssetIcon(type);
  };

  const handleEditAssetInfo = (asset) => {
    setEditingAssetId(asset.id);
    setEditDescription(asset.description || '');
    setEditUsage(asset.usage || '');
  };

  const handleSaveAssetInfo = (assetId) => {
    if (onAssetInfoUpdate) {
      onAssetInfoUpdate(assetId, 'description', editDescription);
      onAssetInfoUpdate(assetId, 'usage', editUsage);
    }
    setEditingAssetId(null);
    setEditDescription('');
    setEditUsage('');
  };

  const handleCancelEdit = () => {
    setEditingAssetId(null);
    setEditDescription('');
    setEditUsage('');
  };

  // Debug logging
  useEffect(() => {
    if (analyzedAssets && analyzedAssets.length > 0) {
      
    }
  }, [analyzedAssets]);

  const mandatoryAssets = analyzedAssets.filter((a) => a && a.mandatory);
  const nonMandatoryAssets = analyzedAssets.filter((a) => a && !a.mandatory);
  const availableAssets = analyzedAssets.filter(
    (a) => a && a.status === 'available'
  );
  const neededAssets = analyzedAssets.filter(
    (a) => a && (a.status === 'needed' || !a.status)
  );


  return (
    <div className="max-h-[calc(100vh-130px)] custom-scrollbar overflow-y-auto bg-zinc-950 text-white p-8">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <h1 className="text-xl font-semibold">Select & Map Assets</h1>
            <Settings className="w-5 h-5 text-gray-400" />
          </div>
          <p className="text-sm text-gray-400">
            Analyze documents, select assets, and provide information for
            storyboard generation.
          </p>
        </div>
        <div className="flex items-center gap-4">
          {analyzedAssets.length > 0 && (
            <div className="flex items-center gap-2 text-sm">
              <div className="bg-red-600 text-white px-3 py-1 rounded-full text-xs font-medium">
                {mandatoryAssets.length} Mandatory
              </div>
              <div className="bg-yellow-600 text-white px-3 py-1 rounded-full text-xs font-medium">
                {nonMandatoryAssets.length} Optional
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Analyze Documents Section */}
      {combinedContent && (
        <div className="bg-zinc-900 rounded-lg p-4 mb-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <FileText className="w-5 h-5 text-blue-400" />
              <div>
                <h3 className="text-sm font-semibold">Document Analysis</h3>
                <p className="text-xs text-gray-400">
                  Analyze uploaded documents to identify required assets
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={onReanalyze}
                disabled={analyzingAssets || autoFetchingAssets}
                className="flex items-center gap-2 bg-red-600 hover:bg-red-700 disabled:bg-zinc-700 disabled:cursor-not-allowed text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors"
              >
                {analyzingAssets ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Analyzing...
                  </>
                ) : (
                  <>
                    <RefreshCw className="w-4 h-4" />
                    {analyzedAssets.length > 0
                      ? 'Re-analyze'
                      : 'Analyze Documents'}
                  </>
                )}
              </button>
              {neededAssets.length > 0 && (
                <button
                  onClick={onAutoFetchAssets}
                  disabled={autoFetchingAssets || analyzingAssets}
                  className="flex items-center gap-2 bg-green-600 hover:bg-green-700 disabled:bg-zinc-700 disabled:cursor-not-allowed text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors"
                  title={`Automatically fetch ${neededAssets.length} needed asset(s) from the internet`}
                >
                  {autoFetchingAssets ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      Fetching...
                    </>
                  ) : (
                    <>
                      <Server className="w-4 h-4" />
                      Auto-Fetch ({neededAssets.length})
                    </>
                  )}
                </button>
              )}
            </div>
          </div>

          {/* Fetch Progress */}
          {fetchProgress && (
            <div className="mt-4 bg-zinc-800 rounded-lg p-3">
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs text-gray-300">
                  {fetchProgress.status === 'fetching' &&
                    `Fetching: ${fetchProgress.asset}`}
                  {fetchProgress.status === 'uploading' &&
                    `Uploading: ${fetchProgress.asset}`}
                  {fetchProgress.status === 'starting' && 'Starting...'}
                </span>
                <span className="text-xs text-gray-400">
                  {fetchProgress.current} / {fetchProgress.total}
                </span>
              </div>
              <div className="w-full bg-zinc-700 rounded-full h-2">
                <div
                  className="bg-green-600 h-2 rounded-full transition-all duration-300"
                  style={{
                    width: `${(fetchProgress.current / fetchProgress.total) * 100}%`,
                  }}
                />
              </div>
            </div>
          )}

        </div>
      )}

      {/* Main Content */}
      <div className="grid grid-cols-2 gap-6">
        {/* Left Panel: Analyzed Assets from Documents */}
        <div className="bg-zinc-900 rounded-lg p-6">
          <div className="flex items-center gap-2 mb-4">
            <Info className="w-5 h-5 text-blue-400" />
            <h2 className="text-lg font-semibold">Analyzed Assets</h2>
          </div>
          <p className="text-xs text-gray-400 mb-4">
            Assets identified from your documents. Add information to help the
            AI understand how to use them.
          </p>

          {analyzedAssets.length === 0 ? (
            <div className="bg-zinc-800 rounded-lg p-8 text-center">
              <FileText className="w-12 h-12 mx-auto mb-3 text-gray-600 opacity-50" />
              <p className="text-sm text-gray-400 mb-2">
                No assets analyzed yet
              </p>
              <p className="text-xs text-gray-500">
                Click "Analyze Documents" to identify assets from your content
              </p>
            </div>
          ) : (
            <div className="space-y-4 max-h-[600px] overflow-y-auto custom-scrollbar">
              {/* Mandatory Assets */}
              {mandatoryAssets.length > 0 && (
                <div>
                  <div className="flex items-center gap-2 mb-2">
                    <AlertCircle className="w-4 h-4 text-red-400" />
                    <h3 className="text-sm font-semibold text-red-400">
                      Mandatory ({mandatoryAssets.length})
                    </h3>
                  </div>
                  <div className="space-y-2">
                    {mandatoryAssets.map((asset) => (
                      <AssetCard
                        key={asset.id}
                        asset={asset}
                        getAssetIcon={getAssetIcon}
                        editingAssetId={editingAssetId}
                        editDescription={editDescription}
                        editUsage={editUsage}
                        setEditDescription={setEditDescription}
                        setEditUsage={setEditUsage}
                        onEdit={handleEditAssetInfo}
                        onSave={handleSaveAssetInfo}
                        onCancel={handleCancelEdit}
                        onAdd={onAddAnalyzedAsset}
                        onAiAutoFetch={onAiAutoFetch}
                        onUpload={onUploadAssetForAnalyzed}
                        onRequiredAdd={onRequiredAssetAdd}
                        isInLoaded={loadedAssets.some((a) => a.id === asset.id)}
                        isInRequired={requiredAssets.some(
                          (a) => a.id === asset.id
                        )}
                        fetchingAssetId={fetchingAssetId}
                        onPreview={setPreviewAsset}
                        onRemove={onAssetRemove}
                      />
                    ))}
                  </div>
                </div>
              )}

              {/* Non-Mandatory Assets */}
              {nonMandatoryAssets.length > 0 && (
                <div>
                  <div className="flex items-center gap-2 mb-2 mt-4">
                    <Info className="w-4 h-4 text-yellow-400" />
                    <h3 className="text-sm font-semibold text-yellow-400">
                      Optional ({nonMandatoryAssets.length})
                    </h3>
                  </div>
                  <div className="space-y-2">
                    {nonMandatoryAssets.map((asset) => (
                      <AssetCard
                        key={asset.id}
                        asset={asset}
                        getAssetIcon={getAssetIcon}
                        editingAssetId={editingAssetId}
                        editDescription={editDescription}
                        editUsage={editUsage}
                        setEditDescription={setEditDescription}
                        setEditUsage={setEditUsage}
                        onEdit={handleEditAssetInfo}
                        onSave={handleSaveAssetInfo}
                        onCancel={handleCancelEdit}
                        onAdd={onAddAnalyzedAsset}
                        onAiAutoFetch={onAiAutoFetch}
                        onUpload={onUploadAssetForAnalyzed}
                        onRequiredAdd={onRequiredAssetAdd}
                        isInLoaded={loadedAssets.some((a) => a.id === asset.id)}
                        isInRequired={requiredAssets.some(
                          (a) => a.id === asset.id
                        )}
                        fetchingAssetId={fetchingAssetId}
                        onPreview={setPreviewAsset}
                        onRemove={onAssetRemove}
                      />
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Middle Panel: Asset Library */}
        <div className="bg-zinc-900 rounded-lg p-6 flex flex-col">
          <div className="flex items-center justify-between mb-1">
            <div className="flex items-center gap-2">
              <Grid3x3 className="w-5 h-5 text-red-600" />
              <h2 className="text-lg font-semibold">Asset Library</h2>
            </div>
            {loadedAssets.length > 0 && (
              <span className="text-xs bg-zinc-700 text-gray-300 px-2 py-0.5 rounded-full">
                {loadedAssets.length} ready
              </span>
            )}
          </div>
          <p className="text-xs text-gray-500 mb-4">
            Assets fetched or uploaded for this training session.
          </p>

          {/* Stats bar — only when assets exist */}
          {loadedAssets.length > 0 && (
            <div className="grid grid-cols-3 gap-2 mb-4">
              {[
                { label: 'Available', count: loadedAssets.filter(a => a.status === 'available').length, color: 'text-green-400' },
                { label: 'Needed', count: loadedAssets.filter(a => a.status === 'needed' || !a.status).length, color: 'text-yellow-400' },
                { label: 'Required', count: requiredAssets.length, color: 'text-red-400' },
              ].map(({ label, count, color }) => (
                <div key={label} className="bg-zinc-800 rounded-lg p-2 text-center">
                  <div className={`text-lg font-bold ${color}`}>{count}</div>
                  <div className="text-xs text-gray-500">{label}</div>
                </div>
              ))}
            </div>
          )}

          {/* From Scene Editor */}
          {gridPageAssets.length > 0 && (
            <div className="mb-4">
              <label className="block text-xs font-semibold text-gray-400 uppercase tracking-wide mb-2">
                From Scene Editor ({gridPageAssets.length})
              </label>
              <div className="bg-zinc-800 rounded-lg p-3 max-h-48 overflow-y-auto custom-scrollbar">
                {loadingGridAssets ? (
                  <div className="flex items-center justify-center py-4">
                    <Loader2 className="w-5 h-5 animate-spin text-gray-400" />
                    <span className="ml-2 text-sm text-gray-400">Loading assets...</span>
                  </div>
                ) : (
                  <div className="space-y-2">
                    {gridPageAssets.map((asset) => {
                      const isAlreadyAdded = loadedAssets.some(
                        (a) => a.id === asset.id && a.type === 'gridpage'
                      );
                      return (
                        <div
                          key={asset.id}
                          className="bg-zinc-700 rounded-lg p-2 flex items-center justify-between hover:bg-zinc-600 transition-colors"
                        >
                          <div className="flex items-center gap-2 flex-1 min-w-0">
                            {getAssetIcon(asset.assetType)}
                            <span className="text-xs text-white truncate flex-1">{asset.name}</span>
                            <span className="text-xs text-gray-400 px-1.5 py-0.5 bg-zinc-600 rounded">
                              {asset.assetType}
                            </span>
                          </div>
                          <button
                            onClick={() => onAddGridPageAsset(asset)}
                            disabled={isAlreadyAdded}
                            className={`ml-2 px-2 py-1 rounded text-xs font-medium transition-colors ${
                              isAlreadyAdded
                                ? 'bg-zinc-600 text-gray-500 cursor-not-allowed'
                                : 'bg-red-600 hover:bg-red-700 text-white'
                            }`}
                          >
                            {isAlreadyAdded ? 'Added' : 'Add'}
                          </button>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Loaded Assets List */}
          {loadedAssets.length > 0 ? (
            <div className="flex-1 min-h-0">
              <label className="block text-xs font-semibold text-gray-400 uppercase tracking-wide mb-2">
                Ready Assets
              </label>
              <div className="space-y-2 max-h-64 overflow-y-auto custom-scrollbar">
                {loadedAssets.map((asset) => (
                  <div
                    key={asset.id}
                    className="bg-zinc-800 rounded-lg p-3 flex items-center justify-between hover:bg-zinc-700 transition-colors gap-2"
                  >
                    <div className="relative w-9 h-9 bg-zinc-700 rounded overflow-hidden flex items-center justify-center flex-shrink-0">
                      {getAssetThumbnail(asset)}
                    </div>
                    <div className="flex flex-col flex-1 min-w-0">
                      <span className="text-sm text-white truncate">{asset.name}</span>
                      <span className={`text-xs ${asset.status === 'available' ? 'text-green-400' : 'text-yellow-400'}`}>
                        {asset.status === 'available' ? 'Available' : 'Needed'}
                      </span>
                    </div>
                    <div className="flex items-center gap-1 flex-shrink-0">
                      {asset.url && (
                        <button
                          onClick={(e) => { e.stopPropagation(); setPreviewAsset(asset); }}
                          className="text-gray-400 hover:text-blue-400 transition-colors"
                          title="Preview"
                        >
                          <Eye className="w-4 h-4" />
                        </button>
                      )}
                      <button
                        onClick={(e) => { e.stopPropagation(); onAssetRemove(asset.id); }}
                        className="text-gray-400 hover:text-red-400 transition-colors"
                        title="Remove"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ) : (
            /* Empty state */
            <div className="flex-1 flex flex-col items-center justify-center text-center py-10 border-2 border-dashed border-zinc-700 rounded-lg">
              <Grid3x3 className="w-10 h-10 text-zinc-600 mb-3" />
              <p className="text-sm font-medium text-gray-400">No assets yet</p>
              <p className="text-xs text-gray-600 mt-1 max-w-[180px]">
                Use <span className="text-blue-400">Fetch &amp; Add</span> or <span className="text-purple-400">Upload</span> on any analyzed asset to populate this library.
              </p>
            </div>
          )}
        </div>
      </div>

      {/* Model Picker Modal */}
      {assetPicker.open && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80" onClick={onCloseAssetPicker}>
          <div className="bg-zinc-900 rounded-xl w-full max-w-3xl mx-4 shadow-2xl flex flex-col overflow-hidden max-h-[90vh]" onClick={e => e.stopPropagation()}>

            {/* Header */}
            <div className="flex items-center justify-between px-6 py-4 border-b border-zinc-800 flex-shrink-0">
              <div>
                <h3 className="text-sm font-semibold text-white">
                  Choose model for &ldquo;{assetPicker.asset?.name}&rdquo;
                </h3>
                <p className="text-xs text-gray-400 mt-0.5">
                  {assetPicker.searchStatus
                    ? assetPicker.searchStatus
                    : assetPicker.searching
                      ? 'Searching…'
                      : `${assetPicker.previews.length} models found — select one, then "Select & Download"`}
                </p>
              </div>
              <button onClick={onCloseAssetPicker} className="text-gray-400 hover:text-white transition-colors">
                <X className="w-5 h-5" />
              </button>
            </div>

            {assetPicker.searching && assetPicker.previews.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-20 gap-3">
                <Loader2 className="w-8 h-8 animate-spin text-red-500" />
                <p className="text-sm text-gray-400">{assetPicker.searchStatus || 'Searching…'}</p>
              </div>
            ) : assetPicker.previews.length === 0 ? (
              <div className="flex items-center justify-center py-20 text-gray-400 text-sm">
                No models found. Try uploading one manually.
              </div>
            ) : (
              <>
                <div className="overflow-y-auto custom-scrollbar flex-1 p-4">
                  <div className="grid grid-cols-3 gap-3">
                    {assetPicker.previews.map((preview, idx) => {
                      const isSelected = assetPicker.selectedPreviewIdx === idx;
                      const isReady = preview.status === 'ready';
                      const isIdle = preview.status === 'idle';
                      const isLoading = preview.status === 'loading';
                      const retryKey = previewRetryKeys[idx] || 0;
                      const thumbOk = preview.thumbnail && !failedThumbnails.has(idx);
                      const embedKey = preview.uid != null ? String(preview.uid) : `card-${idx}`;
                      return (
                        <div
                          key={embedKey}
                          onClick={() => { if (!isLoading) onSelectPreview(idx); }}
                          className={`group bg-zinc-800 rounded-lg overflow-hidden border-2 transition-all cursor-pointer ${
                            isSelected ? 'border-red-500 ring-1 ring-red-500/40'
                              : 'border-transparent hover:border-zinc-500'
                          }`}
                        >
                          {/* Thumbnail / viewer area */}
                          <div className="h-32 bg-zinc-900 relative flex items-center justify-center overflow-hidden">
                            {/* Source badge */}
                            <div className={`absolute bottom-1.5 right-1.5 z-10 text-[9px] font-bold px-1.5 py-0.5 rounded ${
                              preview.source === 'library'
                                ? 'bg-green-700/90 text-green-100'
                                : 'bg-blue-700/80 text-blue-100'
                            }`}>
                              {preview.source === 'library' ? 'LOCAL' : 'SKETCHFAB'}
                            </div>
                            {/* Embed preview — Three.js viewer for local (uses CloudFront URL), Sketchfab iframe for remote */}
                            {activeEmbedKey === embedKey ? (
                              preview.source === 'library' ? (
                                preview.fileUrl ? (
                                  <VisibleMeshPreview
                                    url={preview.fileUrl}
                                    height="128px"
                                    retryKey={retryKey}
                                    onBlobReady={(bUrl) => { blobUrlCacheRef.current[embedKey] = bUrl; }}
                                  />
                                ) : (
                                  <div className="text-xs text-gray-400 px-2 text-center">No file URL</div>
                                )
                              ) : (
                                <iframe
                                  title={preview.name}
                                  src={`https://sketchfab.com/models/${preview.uid}/embed?autostart=1&ui_hint=0`}
                                  allow="autoplay; fullscreen; xr-spatial-tracking"
                                  xr-spatial-tracking="true"
                                  execution-while-out-of-viewport="true"
                                  execution-while-not-rendered="true"
                                  web-share="true"
                                  className="w-full h-full border-0"
                                />
                              )
                            ) : isLoading ? (
                              <div className="text-center">
                                <Loader2 className="w-6 h-6 animate-spin text-blue-400 mx-auto mb-1" />
                                <p className="text-xs text-gray-400">Downloading…</p>
                              </div>
                            ) : isReady && preview.blobUrl ? (
                              /* Local items after download OR Sketchfab after download: Three.js viewer */
                              <VisibleMeshPreview url={preview.blobUrl} height="128px" retryKey={retryKey} />
                            ) : preview.status === 'error' ? (
                              <div className="text-center px-2 flex flex-col items-center">
                                <AlertCircle className="w-5 h-5 text-red-400 mb-1" />
                                <p className="text-xs text-red-400">Failed</p>
                              </div>
                            ) : thumbOk ? (
                              <img
                                src={preview.thumbnail}
                                alt={preview.name}
                                className="w-full h-full object-cover"
                                onError={() => setFailedThumbnails(prev => new Set([...prev, idx]))}
                              />
                            ) : (
                              <Box className="w-7 h-7 text-zinc-600" />
                            )}
                            {isSelected && <div className="absolute top-1.5 right-1.5"><CheckCircle2 className="w-4 h-4 text-red-400 drop-shadow" /></div>}
                            <button
                              onClick={e => { e.stopPropagation(); setExpandedPreviewIdx(idx); }}
                              className="absolute top-1.5 left-1.5 p-1 bg-black/60 hover:bg-black/80 rounded text-white opacity-0 group-hover:opacity-100 transition-opacity"
                            >
                              <Maximize2 className="w-3 h-3" />
                            </button>
                          </div>

                          {/* Info + action */}
                          <div className="px-2.5 pt-1.5 pb-2">
                            <p className="text-xs font-medium text-white truncate">{preview.name}</p>
                            <div className="flex items-center justify-between mt-0.5 mb-2">
                              <p className="text-xs text-gray-500 truncate flex-1">{preview.author}</p>
                              {preview.score != null && <span className="text-xs text-green-400 font-medium ml-1 flex-shrink-0">{preview.score.toFixed(1)}</span>}
                            </div>
                            {/* Select button — always shown (idle, ready, error) */}
                            {!isLoading && (
                              <button
                                onClick={e => { e.stopPropagation(); onSelectPreview(idx); }}
                                className={`w-full flex items-center justify-center gap-1.5 text-xs px-2 py-1.5 rounded transition-colors ${
                                  isSelected ? 'bg-red-600 hover:bg-red-700 text-white' : 'bg-zinc-700 hover:bg-zinc-600 text-white'
                                }`}
                              >
                                <CheckCircle2 className="w-3 h-3" />
                                {isSelected ? 'Selected ✓' : 'Select'}
                              </button>
                            )}
                            {/* Preview 3D button */}
                            {!isLoading && !isReady && (
                              <button
                                onClick={e => {
                                  e.stopPropagation();
                                  setActiveEmbedKey(prev => prev === embedKey ? null : embedKey);
                                }}
                                className="w-full flex items-center justify-center gap-1.5 text-xs bg-zinc-800 hover:bg-zinc-700 text-gray-300 px-2 py-1 rounded transition-colors mt-1"
                              >
                                <Eye className="w-3 h-3" />
                                {activeEmbedKey === embedKey ? 'Hide Preview' : 'Preview 3D'}
                              </button>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>

                <div className="px-6 py-4 border-t border-zinc-800 flex-shrink-0">
                  <button
                    onClick={onConfirmSelectedPreview}
                    disabled={assetPicker.selectedPreviewIdx === null || assetPicker.previews[assetPicker.selectedPreviewIdx]?.status === 'loading'}
                    className="w-full bg-red-600 hover:bg-red-700 disabled:bg-zinc-700 disabled:cursor-not-allowed text-white py-2.5 rounded-lg text-sm font-medium flex items-center justify-center gap-2 transition-colors"
                  >
                    <Plus className="w-4 h-4" />
                    {assetPicker.previews[assetPicker.selectedPreviewIdx]?.status === 'loading'
                      ? 'Downloading…'
                      : assetPicker.previews[assetPicker.selectedPreviewIdx]?.status === 'ready'
                        ? 'Use This Model'
                        : 'Select & Download'}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* Expanded Preview Modal — Sketchfab embed, works for any card */}
      {expandedPreviewIdx !== null && assetPicker.previews[expandedPreviewIdx] && (() => {
        const ep = assetPicker.previews[expandedPreviewIdx];
        const epEmbedKey = ep.uid != null ? String(ep.uid) : `card-${expandedPreviewIdx}`;
        const cachedBlobUrl = blobUrlCacheRef.current[epEmbedKey] || null;
        const isSelected = assetPicker.selectedPreviewIdx === expandedPreviewIdx;
        return (
          <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/90" onClick={() => setExpandedPreviewIdx(null)}>
            <div className="bg-zinc-900 rounded-xl w-full max-w-4xl mx-4 shadow-2xl overflow-hidden flex flex-col" onClick={e => e.stopPropagation()}>
              <div className="flex items-center justify-between px-6 py-3 border-b border-zinc-800">
                <div>
                  <p className="text-sm font-semibold text-white truncate max-w-sm">{ep.name}</p>
                  <div className="flex items-center gap-3 mt-0.5">
                    <p className="text-xs text-gray-400">{ep.author}</p>
                    {ep.score != null && <span className="text-xs text-green-400 font-medium">Score {ep.score.toFixed(1)}</span>}
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => { onSelectPreview(expandedPreviewIdx); setExpandedPreviewIdx(null); }}
                    className={`px-4 py-1.5 rounded text-xs font-medium transition-colors ${isSelected ? 'bg-green-600 hover:bg-green-700 text-white' : 'bg-red-600 hover:bg-red-700 text-white'}`}
                  >
                    {isSelected ? 'Selected ✓' : 'Select This Model'}
                  </button>
                  <button onClick={() => setExpandedPreviewIdx(null)} className="text-gray-400 hover:text-white transition-colors">
                    <X className="w-5 h-5" />
                  </button>
                </div>
              </div>
              <div className="h-[65vh] bg-zinc-950">
                {ep.source === 'library' ? (
                  (ep.fileUrl || cachedBlobUrl) ? (
                    /* Modal is always fully in view — use MeshPreviewViewer directly, skip IntersectionObserver.
                       If the card already downloaded the file, pass the cached blob URL to skip re-download. */
                    <Suspense fallback={
                      <div className="flex flex-col items-center justify-center h-full gap-2">
                        <Loader2 className="w-8 h-8 animate-spin text-blue-400" />
                        <p className="text-xs text-gray-400">Loading 3D…</p>
                      </div>
                    }>
                      <MeshPreviewViewer
                        url={cachedBlobUrl || ep.fileUrl}
                        style={{ height: '65vh' }}
                        onBlobReady={(bUrl) => { blobUrlCacheRef.current[epEmbedKey] = bUrl; }}
                      />
                    </Suspense>
                  ) : (
                    <div className="flex items-center justify-center h-full text-gray-400 text-sm">No file URL available</div>
                  )
                ) : (
                  /* Sketchfab — iframe embed */
                  <iframe
                    title={ep.name}
                    src={`https://sketchfab.com/models/${ep.uid}/embed?autostart=1&ui_hint=0`}
                    allow="autoplay; fullscreen; xr-spatial-tracking"
                    xr-spatial-tracking="true"
                    execution-while-out-of-viewport="true"
                    execution-while-not-rendered="true"
                    web-share="true"
                    className="w-full h-full border-0"
                  />
                )}
              </div>
            </div>
          </div>
        );
      })()}

      {/* Asset Preview Modal */}
      {previewAsset && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/80"
          onClick={() => setPreviewAsset(null)}
        >
          <div
            className="bg-zinc-900 rounded-xl p-6 max-w-3xl w-full mx-4 shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                {getAssetIcon(previewAsset.assetType || previewAsset.type)}
                <h3 className="text-sm font-semibold text-white truncate max-w-xs">
                  {previewAsset.name}
                </h3>
                <span className="text-xs px-2 py-0.5 bg-zinc-700 rounded text-gray-300">
                  {previewAsset.assetType || previewAsset.type}
                </span>
              </div>
              <button
                onClick={() => setPreviewAsset(null)}
                className="text-gray-400 hover:text-white transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="flex items-center justify-center bg-zinc-800 rounded-lg overflow-hidden min-h-[240px] max-h-[70vh]">
              {(previewAsset.assetType === 'image' || previewAsset.type === 'image') && previewAsset.url ? (
                <img
                  src={previewAsset.url}
                  alt={previewAsset.name}
                  className="max-h-[60vh] max-w-full object-contain"
                  onError={(e) => { e.target.replaceWith(Object.assign(document.createElement('p'), { className: 'text-gray-400 text-sm p-8', textContent: 'Preview unavailable' })); }}
                />
              ) : (previewAsset.assetType === 'video' || previewAsset.type === 'video') && previewAsset.url ? (
                <video
                  src={previewAsset.url}
                  controls
                  autoPlay
                  className="max-h-[60vh] max-w-full rounded"
                >
                  Your browser does not support video playback.
                </video>
              ) : (previewAsset.assetType === 'audio' || previewAsset.type === 'audio') && previewAsset.url ? (
                <div className="flex flex-col items-center gap-4 p-8">
                  <Headphones className="w-16 h-16 text-yellow-400" />
                  <p className="text-sm text-gray-300">{previewAsset.name}</p>
                  <audio controls src={previewAsset.url} className="w-full max-w-sm">
                    Your browser does not support audio playback.
                  </audio>
                </div>
              ) : (previewAsset.assetType === 'mesh' || previewAsset.type === 'mesh') ? (
                <Suspense fallback={
                  <div className="flex flex-col items-center gap-3 p-8">
                    <Loader2 className="w-10 h-10 text-blue-400 animate-spin" />
                    <p className="text-sm text-gray-400">Loading 3D model…</p>
                  </div>
                }>
                  <MeshPreviewViewer url={previewAsset.url} style={{ height: '60vh' }} />
                </Suspense>
              ) : (
                <div className="flex flex-col items-center gap-3 p-8">
                  {getAssetIcon(previewAsset.assetType || previewAsset.type)}
                  <p className="text-sm text-gray-400">No preview available</p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Navigation Buttons */}
      <div className="flex justify-between mt-6">
        <button
          onClick={onBack}
          className="flex items-center text-sm text-gray-300 hover:text-white transition-colors"
        >
          <ChevronLeft className="w-4 h-4 mr-1" />
          Back
        </button>
        <button
          onClick={onNext}
          className="bg-red-600 hover:bg-red-700 text-white px-6 py-2 rounded text-sm font-medium flex items-center transition-colors"
        >
          Generate Story
          <ChevronRight className="w-4 h-4 ml-1" />
        </button>
      </div>
    </div>
  );
}

// Asset Card Component
function AssetCard({
  asset,
  getAssetIcon,
  editingAssetId,
  editDescription,
  editUsage,
  setEditDescription,
  setEditUsage,
  onEdit,
  onSave,
  onCancel,
  onAdd,
  onAiAutoFetch,
  onUpload,
  onRequiredAdd,
  isInLoaded,
  isInRequired,
  fetchingAssetId,
  onPreview,
  onRemove,
}) {
  const isEditing = editingAssetId === asset.id;
  const isFetching = fetchingAssetId === asset.id;
  const fileInputRef = useRef(null);
  const type = asset.type || asset.assetType;

  const handleUploadClick = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    if (file && onUpload) {
      onUpload(asset, file);
    }
    // Reset input value to allow re-uploading the same file
    if (e.target) {
      e.target.value = '';
    }
  };

  return (
    <div
      className={`bg-zinc-800 rounded-lg p-3 border ${
        asset.mandatory ? 'border-red-500/50' : 'border-yellow-500/50'
      }`}
    >
      <div className="flex items-start justify-between mb-2">
        <div className="flex items-center gap-2 flex-1 min-w-0">
          {/* Thumbnail */}
          <div className="relative w-10 h-10 bg-zinc-700 rounded overflow-hidden flex items-center justify-center flex-shrink-0">
            {type === 'image' && asset.url ? (
              <img src={asset.url} alt={asset.name} className="w-full h-full object-cover"
                onError={(e) => { e.target.style.display = 'none'; }} />
            ) : type === 'video' && asset.url ? (
              <>
                <video src={asset.url} className="w-full h-full object-cover" muted preload="metadata" />
                <div className="absolute inset-0 flex items-center justify-center bg-black/40">
                  <Play className="w-3 h-3 text-white" />
                </div>
              </>
            ) : type === 'audio' ? (
              <Headphones className="w-5 h-5 text-yellow-400" />
            ) : (
              getAssetIcon(type)
            )}
          </div>

          <div className="flex-1 min-w-0">
            <div className="text-sm font-medium text-white truncate">
              {asset.name}
            </div>
            <div className="flex items-center gap-2 mt-1">
              <span className="text-xs px-1.5 py-0.5 bg-zinc-700 rounded text-gray-300">
                {type || 'mesh'}
              </span>
              {asset.status === 'available' ? (
                <span className="text-xs px-1.5 py-0.5 bg-green-600/20 text-green-400 rounded flex items-center gap-1">
                  <CheckCircle2 className="w-3 h-3" />
                  Available
                </span>
              ) : (
                <span className="text-xs px-1.5 py-0.5 bg-yellow-600/20 text-yellow-400 rounded flex items-center gap-1">
                  <AlertCircle className="w-3 h-3" />
                  Needed
                </span>
              )}
            </div>
          </div>
        </div>
        <div className="flex items-center gap-1 flex-shrink-0">
          {!isEditing && asset.url && onPreview && (
            <button
              onClick={() => onPreview(asset)}
              className="p-1 text-gray-400 hover:text-blue-400 transition-colors"
              title="Preview asset"
            >
              <Eye className="w-3 h-3" />
            </button>
          )}
          {!isEditing && (
            <button
              onClick={() => onEdit(asset)}
              className="p-1 text-gray-400 hover:text-white transition-colors"
              title="Edit information"
            >
              <Edit2 className="w-3 h-3" />
            </button>
          )}
          {!isEditing && asset.status !== 'available' && onRemove && (
            <button
              onClick={() => onRemove(asset.id)}
              className="p-1 text-gray-500 hover:text-red-400 transition-colors"
              title="Remove asset"
            >
              <X className="w-3 h-3" />
            </button>
          )}
        </div>
      </div>

      {isEditing ? (
        <div className="space-y-2 mt-2">
          <div>
            <label className="block text-xs text-gray-400 mb-1">
              Description
            </label>
            <textarea
              value={editDescription}
              onChange={(e) => setEditDescription(e.target.value)}
              className="w-full bg-zinc-900 text-white text-xs rounded px-2 py-1 focus:outline-none focus:ring-2 focus:ring-red-500"
              rows={2}
              placeholder="What is this asset?"
            />
          </div>
          <div>
            <label className="block text-xs text-gray-400 mb-1">
              Usage in Training
            </label>
            <textarea
              value={editUsage}
              onChange={(e) => setEditUsage(e.target.value)}
              className="w-full bg-zinc-900 text-white text-xs rounded px-2 py-1 focus:outline-none focus:ring-2 focus:ring-red-500"
              rows={2}
              placeholder="How will this asset be used?"
            />
          </div>
          <div className="flex gap-2">
            <button
              onClick={() => onSave(asset.id)}
              className="flex-1 bg-red-600 hover:bg-red-700 text-white px-2 py-1 rounded text-xs font-medium flex items-center justify-center gap-1"
            >
              <Save className="w-3 h-3" />
              Save
            </button>
            <button
              onClick={onCancel}
              className="flex-1 bg-zinc-700 hover:bg-zinc-600 text-white px-2 py-1 rounded text-xs font-medium"
            >
              Cancel
            </button>
          </div>
        </div>
      ) : (
        <>
          {asset.description && (
            <p className="text-xs text-gray-400 mb-1 line-clamp-2">
              {asset.description}
            </p>
          )}
          {asset.usage && (
            <p className="text-xs text-gray-500 line-clamp-1">
              Usage: {asset.usage}
            </p>
          )}
          <div className="flex flex-col gap-1.5 mt-2">
            <input
              ref={fileInputRef}
              type="file"
              className="hidden"
              accept={ASSET_FILE_TYPES}
              onChange={handleFileChange}
            />
            {!isInLoaded ? (
              <>
                {/* Row 1: Let AI Decide + Browse (manual picker) */}
                <div className="flex gap-1.5">
                  <button
                    onClick={() => onAiAutoFetch && onAiAutoFetch(asset)}
                    disabled={isFetching}
                    className="flex-1 bg-gradient-to-r from-violet-600 to-indigo-600 hover:from-violet-500 hover:to-indigo-500 disabled:from-zinc-700 disabled:to-zinc-700 disabled:cursor-not-allowed text-white px-2 py-1.5 rounded text-xs font-medium flex items-center justify-center gap-1"
                    title="Let AI pick and download the best match automatically"
                  >
                    {isFetching ? (
                      <><Loader2 className="w-3 h-3 animate-spin" />Working…</>
                    ) : (
                      <><Zap className="w-3 h-3" />Let AI Decide</>
                    )}
                  </button>
                  <button
                    onClick={() => onAdd && onAdd(asset)}
                    disabled={isFetching}
                    className="flex-1 bg-blue-600 hover:bg-blue-700 disabled:bg-zinc-700 disabled:cursor-not-allowed text-white px-2 py-1.5 rounded text-xs font-medium flex items-center justify-center gap-1"
                    title="Browse and pick a model manually"
                  >
                    <Server className="w-3 h-3" />
                    Browse
                  </button>
                </div>
                {/* Row 2: Upload */}
                <button
                  onClick={handleUploadClick}
                  disabled={isFetching}
                  className="w-full bg-purple-600 hover:bg-purple-700 disabled:bg-zinc-700 disabled:cursor-not-allowed text-white px-2 py-1.5 rounded text-xs font-medium flex items-center justify-center gap-1"
                  title="Upload asset file from your computer"
                >
                  <Upload className="w-3 h-3" />
                  Upload
                </button>
              </>
            ) : (
              <button
                onClick={handleUploadClick}
                className="w-full bg-purple-600 hover:bg-purple-700 text-white px-2 py-1 rounded text-xs font-medium flex items-center justify-center gap-1"
                title="Replace with uploaded file"
              >
                <Upload className="w-3 h-3" />
                Replace
              </button>
            )}
          </div>
        </>
      )}
    </div>
  );
}
